import React from 'react'
import { StandardColumn } from '../../../../components/StandardPage'
import { OverflowCell } from '../../../../components/OverflowCell'

export const compositionScreenKey = 'composition_product'

export interface ProductListItem {
  id: number
  code: string
  name: string
  referenceProduct?: string | null
  description?: string | null
  price?: number | null
  graduationType?: string | null
  supplierCode?: string | null
  observations?: string | null
  clientIdentification?: string | null
  composition?: string | null
  annotation?: string | null
  approvals?: string | null
  measures?: string | null
  step?: string | null
  tags?: Array<{ id: number; name: string; color?: string }>
  collection: string
  episode: string
  family: string
  grades: string
  groups: string
  colors: string
  coverImageUrl: string | null
  coverImageScale: number | null
  coverImagePosX: number | null
  coverImagePosY: number | null
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const getFirstWithEllipsis = (content: string): string => {
  if (!content) return '-'
  const parts = content.split(',').map((s) => s.trim()).filter(Boolean)
  if (parts.length === 0) return '-'
  if (parts.length === 1) return parts[0]
  return `${parts[0]}...`
}

const buildTooltipText = (content: string): string => {
  if (!content) return ''
  const items = content.split(',').map((s) => s.trim()).filter(Boolean)
  return items.length > 1 ? items.join('\n') : content
}

const summarizedCell = (value?: string | null) => {
  const str = value || '-'
  return (
    <OverflowCell text={buildTooltipText(str)} forceTooltip={str.includes(',') || str.length > 20}>
      {getFirstWithEllipsis(str)}
    </OverflowCell>
  )
}

export const buildCompositionColumns = (): StandardColumn<ProductListItem>[] => [
  { id: 'code', label: 'Código' },
  { id: 'name', label: 'Produto' },
  {
    id: 'referenceProduct',
    label: 'Produto referência',
    cell: (product) => summarizedCell(product.referenceProduct),
  },
  {
    id: 'description',
    label: 'Descrição',
    cell: (product) => summarizedCell(product.description),
  },
  {
    id: 'price',
    label: 'Valor',
    accessor: (product) => product.price ?? 0,
    cell: (product) => <>{formatCurrency(product.price)}</>,
    exportValue: (product) => formatCurrency(product.price),
  },
  {
    id: 'graduationType',
    label: 'Tipo de graduação',
    cell: (product) => summarizedCell(product.graduationType),
  },
  {
    id: 'supplierCode',
    label: 'Código do fornecedor',
    cell: (product) => summarizedCell(product.supplierCode),
  },
  {
    id: 'observations',
    label: 'Observações',
    cell: (product) => summarizedCell(product.observations),
  },
  {
    id: 'clientIdentification',
    label: 'Identificação do cliente',
    cell: (product) => summarizedCell(product.clientIdentification),
  },
  {
    id: 'composition',
    label: 'Composição',
    cell: (product) => summarizedCell(product.composition),
  },
  {
    id: 'annotation',
    label: 'Anotação',
    cell: (product) => summarizedCell(product.annotation),
  },
  {
    id: 'approvals',
    label: 'Aprovações',
    cell: (product) => summarizedCell(product.approvals),
  },
  {
    id: 'measures',
    label: 'Medidas',
    cell: (product) => summarizedCell(product.measures),
  },
  {
    id: 'step',
    label: 'Etapa',
    cell: (product) => summarizedCell(product.step),
  },
  { id: 'family', label: 'Família', cell: (product) => summarizedCell(product.family) },
  { id: 'grades', label: 'Grades', cell: (product) => summarizedCell(product.grades) },
  { id: 'groups', label: 'Grupos', cell: (product) => summarizedCell(product.groups) },
  { id: 'colors', label: 'Cores', cell: (product) => summarizedCell(product.colors) },
  { id: 'collection', label: 'Coleção', defaultHidden: true, cell: (product) => summarizedCell(product.collection) },
  { id: 'episode', label: 'Episódio', defaultHidden: true, cell: (product) => summarizedCell(product.episode) },
]
